import { classifyRepositoryPath } from "./repositoryClassifier";

import {
  detectIntegrationSignals,
  detectStackSignals,
} from "./repositorySignalDetector";

import type {
  ProjectSweepEvidence,
  RepositoryArtifact,
  RepositoryInventory,
} from "./types";

export interface RepositoryFileEntry {
  path: string;
  sizeBytes?: number | null;
}

export interface RepositorySnapshotReader {
  getRoot(): Promise<string>;
  listFiles(): Promise<readonly RepositoryFileEntry[]>;
}

function extensionOf(path: string): string | null {
  const name = path.split("/").pop() ?? path;

  const index = name.lastIndexOf(".");

  if (index <= 0 || index === name.length - 1) {
    return null;
  }

  return name.slice(index + 1).toLowerCase();
}

export class RepositorySweepService {
  constructor(private readonly reader: RepositorySnapshotReader) {}

  async sweep(): Promise<ProjectSweepEvidence> {
    const root = await this.reader.getRoot();

    const entries = await this.reader.listFiles();

    const artifacts: RepositoryArtifact[] = entries.map((entry) => ({
      path: entry.path,
      kind: classifyRepositoryPath(entry.path),
      extension: extensionOf(entry.path),
      sizeBytes: entry.sizeBytes ?? null,
    }));

    const paths = artifacts.map((artifact) => artifact.path);

    const select = (kind: RepositoryArtifact["kind"]) =>
      artifacts
        .filter((artifact) => artifact.kind === kind)
        .map((artifact) => artifact.path);

    const inventory: RepositoryInventory = {
      root,
      artifacts,
      stack: detectStackSignals(paths),
      integrations: detectIntegrationSignals(paths),
      specs: select("spec"),
      documentation: select("documentation"),
      workflows: select("workflow"),
      migrations: select("migration"),
      tests: select("test"),
      configurations: select("configuration"),
    };

    const unknownFiles = artifacts.filter(
      (artifact) => artifact.kind === "unknown",
    ).length;

    return {
      inventory,
      totalFiles: artifacts.length,
      classifiedFiles: artifacts.length - unknownFiles,
      unknownFiles,
      evidenceComplete: true,
    };
  }
}
